/**
 * This is a fun little hack to render the cookie declaration
 *
 * When attempting to follow the CookieBot implementation guide, we have issues
 * as the script is rendered into a web component and hence a shadow dom, so refs
 * in the script fail as the context isn't the entire DOM, just the current shadow dom
 *
 * Instead, this function runs outside the DOM, adds the script in a hidden div to render
 * the cookie declaration, then updates the route content, then asks the view to update
 *
 * */

import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { Content } from '../entities';

export function cookiebot(content: Content) {
  const renderElmId = 'CookiebotRenderElement';

  // because we update the _content object, this only needs to run
  // once - on subsequent visits the the content will exist
  if (document.getElementById(`${renderElmId}`)) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent('content-view-loading', { bubbles: true, composed: true })
  );

  function callback(mutationList: Array<MutationRecord>) {
    for (const mutation of mutationList) {
      if (mutation.type !== 'childList' || !mutation.addedNodes.length) return;

      const targetId = (mutation.target as HTMLElement).id;
      if (targetId === renderElmId) {

        const html = (mutation.addedNodes[0] as HTMLElement).outerHTML;
        content.html = unsafeHTML(html);

        window.dispatchEvent(
          new CustomEvent('content-view-data-updated', {
            bubbles: true,
            composed: true,
            detail: {
              content,
            },
          })
        );
      }
    }
  }

  const renderElm = document.createElement('div');
  renderElm.id = renderElmId;
  renderElm.style.display = 'none';
  document.body.appendChild(renderElm);

  const scriptElm = document.createElement('script');
  scriptElm.id = 'CookieDeclaration';
  scriptElm.async = true;
  scriptElm.src =
    'https://consent.cookiebot.com/5d9b509b-d030-4a2b-9f3a-1d0e772764c0/cd.js';

  renderElm.appendChild(scriptElm);

  const observer = new MutationObserver(callback);
  const config: MutationObserverInit = { childList: true, subtree: true };

  observer.observe(document.body, config);

  // listener needs to be removed to avoid polluting global scope
  // observer is disconnected when routing away from /cookies
  const disconnect = (e: CustomEvent) => {
    if (e.detail.location.pathname !== '/cookies') {
      observer.disconnect();
      window.removeEventListener('vaadin-router-location-changed', disconnect);
    }
  };

  window.addEventListener('vaadin-router-location-changed', disconnect);
}
